import { action, computed, makeObservable, observable } from 'mobx';
import { apiService } from 'config/ioc';
import userStore from './UserStore';

const FINANCE_REDIRECT_SALES_ORGANIZATION_ID = '7009';
const DPP_METRICS_ROUTE = '/dpp-metrics';

interface Permissions {
  canViewFinancePage: boolean;
  canSetAnyUserForFinance: boolean;
  canViewDppPages: boolean;
  isCanViewJuncerFinances: boolean;
  canViewPromisesPage: boolean;
}

interface PermissionsResponse {
  data?: {
    data?: Partial<Permissions>;
  } & Partial<Permissions>;
}

class PermissionsStore {
  canViewFinancePage = false;
  isInitialized = false;
  isLoading = false;
  canSetAnyUserForFinance = false;
  canViewDppPages = false;
  isCanViewJuncerFinances = false;
  canViewPromisesPage = false;

  constructor() {
    makeObservable(this, {
      canViewFinancePage: observable,
      isInitialized: observable,
      isLoading: observable,
      canViewDppPages: observable,
      isCanViewJuncerFinances: observable,
      canViewPromisesPage: observable,
      hasFinanceAccess: computed,
      forcedRoute: computed,
      shouldRedirectToFinance: computed,
      initialize: action.bound,
      _setPermissions: action.bound,
    });
  }

  get hasFinanceAccess() {
    return this.canViewFinancePage;
  }

  get forcedRoute() {
    if (this.canViewDppPages) {
      return DPP_METRICS_ROUTE;
    }

    return null;
  }

  get shouldRedirectToFinance() {
    if (!this.hasFinanceAccess) {
      return false;
    }

    const { user } = userStore;

    return (
      user?.salesOrganization?.id === FINANCE_REDIRECT_SALES_ORGANIZATION_ID
    );
  }

  _setPermissions({
    canViewFinancePage,
    canSetAnyUserForFinance,
    canViewDppPages,
    isCanViewJuncerFinances,
    canViewPromisesPage,
  }: Permissions): void {
    this.canViewFinancePage = canViewFinancePage;
    this.canSetAnyUserForFinance = canSetAnyUserForFinance;
    this.canViewDppPages = canViewDppPages;
    this.isCanViewJuncerFinances = isCanViewJuncerFinances;
    this.canViewPromisesPage = canViewPromisesPage;
  }

  _fetch(): Promise<void> {
    this.isLoading = true;

    return apiService
      .get('/external_data/permissions/')
      .then((response: PermissionsResponse) => {
        const data = response?.data?.data ?? response?.data ?? {};
        this._setPermissions({
          canViewFinancePage: !!data.canViewFinancePage,
          canSetAnyUserForFinance: !!data.canSetAnyUserForFinance,
          canViewDppPages: !!data.canViewDppPages,
          isCanViewJuncerFinances: !!data.isCanViewJuncerFinances,
          canViewPromisesPage: !!data.canViewPromisesPage,
        });
      })
      .catch(() => {
        this._setPermissions({
          canViewFinancePage: false,
          canSetAnyUserForFinance: false,
          canViewDppPages: false,
          isCanViewJuncerFinances: false,
          canViewPromisesPage: false,
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  initialize(): Promise<void> {
    if (this.isInitialized) return Promise.resolve();
    this.isInitialized = true;
    return this._fetch();
  }

  refetch(): Promise<void> {
    if (!this.isInitialized) {
      this.isInitialized = true;
    }

    return this._fetch();
  }

  getInitialRoute(): string {
    if (this.forcedRoute) {
      return this.forcedRoute;
    }

    if (this.shouldRedirectToFinance) {
      return '/finance';
    }
    return '/';
  }

  shouldShowFinanceTab(): boolean {
    return this.hasFinanceAccess;
  }

  shouldShowDeliveriesTab(): boolean {
    return !this.hasFinanceAccess;
  }

  canAccessFinance(): boolean {
    return this.hasFinanceAccess;
  }
}

const permissionsStore = new PermissionsStore();

export default permissionsStore;
